import React, { useState, useEffect } from "react";
import { Modal, message, Form, InputNumber, Input, Button, Card, Row, Col, Alert, Spin, Tooltip, Tag, Checkbox, Switch, TimePicker, Select, Space, Divider, } from "antd";
import dayjs from "dayjs";
import { SettingIcon, ReTryIcon, ChartIcon, ClockIcon } from "../Icons";
import { useGlobal } from "../../global/user";
import { Gas, GasMonitor } from "../../api";
import GasChart from "../chart/GasChart";
import "./function.css";


const SENSOR_NAME_SUGGESTIONS = [
  "temp",
  "hum", 
  "O2",
  "CO",
  "LEL",
  "H2S",
  "CH4",
  "CO2"
];

const SENSOR_MAPPING = {
  sensor1: { dbKey: 'a', dataKey: 'a_val' },
  sensor2: { dbKey: 'b', dataKey: 'b_val' },
  sensor3: { dbKey: 'c', dataKey: 'c_val' },
  sensor4: { dbKey: 'd', dataKey: 'd_val' },
  sensor5: { dbKey: 'e', dataKey: 'e_val' },
  sensor6: { dbKey: 'f', dataKey: 'f_val' },
  sensor7: { dbKey: 'g', dataKey: 'g_val' },
  sensor8: { dbKey: 'h', dataKey: 'h_val' }
};

// 星期 bitmask：value 對應 Python 的 datetime.weekday()，週一=0 ... 週日=6。
// 切勿改用 JS 的 getDay()（週日=0），那會讓 bitmask 整個位移一天且不會報錯。
const WEEKDAY_OPTIONS = [
  { label: "一", value: 0 },
  { label: "二", value: 1 },
  { label: "三", value: 2 },
  { label: "四", value: 3 },
  { label: "五", value: 4 },
  { label: "六", value: 5 },
  { label: "日", value: 6 },
];

const weekdaysToBitmask = (days) =>
  (days || []).reduce((mask, d) => mask | (1 << d), 0);

const bitmaskToWeekdays = (mask) =>
  [0, 1, 2, 3, 4, 5, 6].filter((d) => ((mask || 0) >> d) & 1);

// start/end 為 null 代表該項不更動，因此「週末不發」與「下班不發」可疊加使用
const ALERT_PRESETS = {
  allDay: { label: "全天候發報", weekdays: [0, 1, 2, 3, 4, 5, 6], start: "00:00", end: "23:59" },
  weekday: { label: "週末不發（一~五）", weekdays: [0, 1, 2, 3, 4], start: null, end: null },
  workHour: { label: "下班時間不發（08:00~18:00）", weekdays: null, start: "08:00", end: "18:00" },
};

const HEAT_RISK_OPTIONS = [
  { label: "不發報", value: 0 },
  { label: "第二級以上（格外注意）", value: 2 },
  { label: "第三級以上（危險）", value: 3 },
  { label: "第四級（極度危險）", value: 4 },
];

const getHeatRiskText = (level) => {
  switch (level) {
    case 0: return <Tag color="blue">未達警戒值 (安全)</Tag>;
    case 1: return <Tag color="green">第一級 (注意)</Tag>;
    case 2: return <Tag color="yellow">第二級 (格外注意)</Tag>;
    case 3: return <Tag color="orange">第三級 (危險)</Tag>;
    case 4: return <Tag color="red">第四級 (極度危險)</Tag>;
    case 5: return <Tag color="gray">未知</Tag>;
    default: return <Tag>未知</Tag>;
  }
};

// 現場人員最容易把發報時段設反，一行預覽可以擋掉大部分誤設
function AlertSchedulePreview({ form }) {
  const enabled = Form.useWatch("alertEnabled", form);
  const cooldown = Form.useWatch("cooldownMinutes", form);
  const weekdays = Form.useWatch("weekdays", form);
  const timeRange = Form.useWatch("timeRange", form);
  const toBuffer = Form.useWatch("sendToBuffer", form);

  if (!enabled) {
    return (
      <Alert
        type="warning"
        showIcon
        message="通知已停用"
        description="此設備即使偵測到異常也不會發送任何通知。"
      />
    );
  }

  const days = weekdays || [];
  const labels = WEEKDAY_OPTIONS.filter((o) => days.includes(o.value)).map((o) => o.label);

  let dayText = "未選擇任何星期（不會發報）";
  if (labels.length === 7) dayText = "每天";
  else if (labels.length > 0) dayText = `週${labels.join("、")}`;

  const start = timeRange?.[0]?.format("HH:mm") || "--:--";
  const end = timeRange?.[1]?.format("HH:mm") || "--:--";
  const crossMidnight =
    timeRange?.[0] && timeRange?.[1] && start > end;

  return (
    <Alert
      type={labels.length === 0 ? "error" : "info"}
      showIcon
      message="目前設定"
      description={
        <>
          <div>
            <b>{dayText}</b> 的 <b>{start} ~ {end}</b> 會發報
            {crossMidnight && (
              <Tag color="purple" style={{ marginLeft: 6 }}>跨午夜</Tag>
            )}
          </div>
          <div>
            同一項目異常時，每 <b>{cooldown || "--"}</b> 分鐘最多發送一則。
          </div>
          {toBuffer && (
            <div style={{ color: "#d46b08" }}>
              <b>警報改送 Buffer 群，現場群組不會收到。</b>
            </div>
          )}
          <div style={{ color: "#888", marginTop: 4 }}>
            時段外發生的異常不會補發；冷卻中的異常會送到 Buffer 群。
          </div>
        </>
      }
    />
  );
}

export default function Function14({ machineID, machineInfo, status, setStatus }) {
  const { user } = useGlobal();
  
  const [state, setState] = useState(null);
  const [sensorConfigs, setSensorConfigs] = useState({});
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  const [onSettingModal, setOnSettingModal] = useState(false);
  const [onChartModal, setOnChartModal] = useState(false);
  
  const [mqttTopic, setMqttTopic] = useState("");
  const [deviceImei, setDeviceImei] = useState("");
  const [showHeatRisk, setShowHeatRisk] = useState(true);
  const [heatRiskMqttTopic, setHeatRiskMqttTopic] = useState("");

  const [imeiValidation, setImeiValidation] = useState({ 
    status: '', 
    message: '', 
    loading: false 
  });

  const [collapsedSensors, setCollapsedSensors] = useState({
    sensor1: false,
    sensor2: false,
    sensor3: false,
    sensor4: false,
    sensor5: false,
    sensor6: false,
    sensor7: false,
    sensor8: false
  });

  // 各感測器是否為雨量計（累積值回傳）；勾選時隱藏校正係數並強制 A=1/B=0
  const [rainFlags, setRainFlags] = useState({});
  // 上限／下限各自的啟用旗標 { [sensorIndex]: { ub: bool, lb: bool } }，控制表單欄位 disabled
  const [boundFlags, setBoundFlags] = useState({});

  const [onAlertModal, setOnAlertModal] = useState(false);
  const [alertLoading, setAlertLoading] = useState(false);
  const [alertSaving, setAlertSaving] = useState(false);
  const [forceBufferActive, setForceBufferActive] = useState(false);

  const [form] = Form.useForm();
  // 必須是獨立的 form 實例。與感測器設定共用會因為 destroyOnClose 互相清空欄位。
  const [alertForm] = Form.useForm();

  useEffect(() => {
    loadInitialData();
  }, [machineID]);

  useEffect(() => {
    if (state && setStatus) {
      const newStatus = state.status ? 1 : 0;
      setStatus(newStatus);
    }
  }, [state, setStatus, machineID]);

  const validateFieldName = (name) => {
    if (!name || name.trim() === '') return true;
    // 排除控制字符、引號、反斜線
    return !/[\p{Cc}"'\\]/u.test(name.trim());
  };

  const loadInitialData = async () => {
    setLoading(true);
    try {
      let deviceConfig = null;
      let isNewDevice = false;
      
      try {
        deviceConfig = await Gas.getDeviceConfig(machineID);
      } catch (error) {
        console.warn("載入設備配置失敗，可能是新設備:", error);
        isNewDevice = true;
      }

      const data = await Gas.fetchGasData(machineID);
      setState(data.info);

      const configs = {};
      const formValues = {};

      const dbSensorNames = {};
      const dbMqttFields = {};

      if (deviceConfig && deviceConfig.sensors) {
        const sensorMappings = {
          a0: 'sensor1', b0: 'sensor2', c0: 'sensor3', d0: 'sensor4',
          e0: 'sensor5', f0: 'sensor6', g0: 'sensor7', h0: 'sensor8'
        };
        
        const valMappings = {
          a_val: 'sensor1', b_val: 'sensor2', c_val: 'sensor3', d_val: 'sensor4',
          e_val: 'sensor5', f_val: 'sensor6', g_val: 'sensor7', h_val: 'sensor8'
        };
        
        Object.keys(valMappings).forEach(dbKey => {
          const sensorKey = valMappings[dbKey];
          const displayName = deviceConfig.sensors[dbKey];
          dbSensorNames[sensorKey] = displayName || '';
        });
        
        Object.keys(sensorMappings).forEach(dbKey => {
          const sensorKey = sensorMappings[dbKey];
          const mqttField = deviceConfig.sensors[dbKey];
          dbMqttFields[sensorKey] = mqttField || '';
        });
      }

      for (let i = 0; i < 8; i++) {
        const sensorKey = `sensor${i + 1}`;
        const dbKey = SENSOR_MAPPING[sensorKey].dbKey;
        const sensor = data.info.sensors[i];
        
        let sensorName = dbSensorNames[sensorKey] || '';
        let mqttField = dbMqttFields[sensorKey] || '';
        
        if (isNewDevice && !sensorName && !mqttField && i < SENSOR_NAME_SUGGESTIONS.length) {
          sensorName = SENSOR_NAME_SUGGESTIONS[i];
          mqttField = SENSOR_NAME_SUGGESTIONS[i];
        }

        let sensorBounds = { upperBound: 100, lowerBound: 0, tolerance: 1, upperEnabled: true, lowerEnabled: true };
        try {
          if (deviceConfig && deviceConfig.bounds && deviceConfig.bounds[dbKey]) {
            const bounds = deviceConfig.bounds[dbKey];
            // 用 ?? 而非 ||：使用者設定 0 時不能被預設值蓋掉
            sensorBounds = {
              upperBound: bounds.upper_bound ?? 100,
              lowerBound: bounds.lower_bound ?? 0,
              tolerance: bounds.tolerance ?? 1,
              upperEnabled: bounds.upper_enabled !== false,
              lowerEnabled: bounds.lower_enabled !== false
            };
          }
        } catch (error) {
          console.warn(`獲取感測器 ${dbKey} 邊界值失敗:`, error);
        }

        let calibrationA = 1;
        let calibrationB = 0;

        if (deviceConfig && deviceConfig.calibration) {
          const aKey = `${dbKey}1`;
          const bKey = `${dbKey}2`;
          
          if (deviceConfig.calibration[aKey] !== undefined) {
            calibrationA = parseFloat(deviceConfig.calibration[aKey]) || 1;
          }
          if (deviceConfig.calibration[bKey] !== undefined) {
            calibrationB = parseFloat(deviceConfig.calibration[bKey]) || 0;
          }
        }
        
        // 雨量計旗標（後端 GetDeviceConfig 回傳 is_rain: { a: bool, ... }）
        const isRain = !!(deviceConfig && deviceConfig.is_rain && deviceConfig.is_rain[dbKey]);

        configs[sensorKey] = {
          name: sensorName,
          mqttField: mqttField,
          value: sensor.value,
          rawValue: sensor.value,
          bounds: sensorBounds,
          calibration: isRain ? { A: 1, B: 0 } : {
            A: calibrationA,
            B: calibrationB
          },
          isRain: isRain,
          dbKey: dbKey
        };
        
        formValues[`sensorName${i + 1}`] = sensorName;
        formValues[`sensorMqtt${i + 1}`] = mqttField;
        formValues[`sensor${i + 1}UpperBound`] = sensorBounds.upperBound;
        formValues[`sensor${i + 1}LowerBound`] = sensorBounds.lowerBound;
        formValues[`sensor${i + 1}Tolerance`] = sensorBounds.tolerance;
        formValues[`sensor${i + 1}UbOn`] = sensorBounds.upperEnabled;
        formValues[`sensor${i + 1}LbOn`] = sensorBounds.lowerEnabled;
        formValues[`${sensorKey}A`] = isRain ? 1 : calibrationA;
        formValues[`${sensorKey}B`] = isRain ? 0 : calibrationB;
        formValues[`sensor${i + 1}IsRain`] = isRain;
      }

      // 同步雨量旗標狀態（控制表單校正欄位的顯示/隱藏）
      const initialRainFlags = {};
      for (let i = 1; i <= 8; i++) {
        initialRainFlags[i] = !!formValues[`sensor${i}IsRain`];
      }
      setRainFlags(initialRainFlags);

      const initialBoundFlags = {};
      for (let i = 1; i <= 8; i++) {
        initialBoundFlags[i] = {
          ub: formValues[`sensor${i}UbOn`] !== false,
          lb: formValues[`sensor${i}LbOn`] !== false
        };
      }
      setBoundFlags(initialBoundFlags);

      const showHeatLines = deviceConfig?.show_heat_lines !== false;

      if (data.info.show_heat_risk === 1 && data.info.sensors[8]) {
        configs['sensor9'] = {
          name: '熱指數',
          mqttField: 'heat_index',
          value: data.info.sensors[8].value,
          rawValue: data.info.sensors[8].value,
          // 熱指數不使用上下限，圖表改畫 2/3/4 級分界虛線
          levelLines: showHeatLines,
          calibration: {
            A: 1,
            B: 0
          },
          dbKey: 'heat_index'
        };
      }

      setSensorConfigs(configs);
      form.setFieldsValue(formValues);
      
      let mqttTopicValue = `iot3be_${machineID}`;
      let deviceImeiValue = "";
      
      if (deviceConfig) {
        if (deviceConfig.mqtt_topic) { mqttTopicValue = deviceConfig.mqtt_topic; }
        if (deviceConfig.device_imei) { deviceImeiValue = deviceConfig.device_imei; }
      }

      if (deviceConfig && deviceConfig.show_heat_risk !== undefined) {
        setShowHeatRisk(deviceConfig.show_heat_risk === 1 || deviceConfig.show_heat_risk === true);
      }
      
      setMqttTopic(mqttTopicValue);
      setDeviceImei(deviceImeiValue);
      setHeatRiskMqttTopic(deviceConfig?.heat_risk_mqtt_topic || "");
      
      form.setFieldsValue({
        ...formValues,
        mqttTopic: mqttTopicValue,
        deviceImei: deviceImeiValue,
        showHeatRisk: deviceConfig?.show_heat_risk !== 0,
        showHeatLines: deviceConfig?.show_heat_lines !== false,
        heatRiskMqttTopic: deviceConfig?.heat_risk_mqtt_topic || ""
      });

    } catch (error) {
      console.error("載入初始資料失敗:", error);
      message.error("載入資料失敗");
      
      if (setStatus) {
        setStatus(0);
      }
    } finally {
      setLoading(false);
    }
  };

  const applyCalibration = (value, calibrationA, calibrationB) => {
    // 跳過無效值
    if (value === null || value === undefined || value === "--" || isNaN(value)) {
      return value;
    }
    
    // A=1,B=0時跳過計算節省資源
    if (calibrationA === 1 && calibrationB === 0) {
      return value;
    }
    
    // 執行校正計算：輸出值 = 原始值 × 係數A + 偏移B
    const calibratedValue = parseFloat(value) * calibrationA + calibrationB;
    return parseFloat(calibratedValue.toFixed(2));
  };

  const validateIMEI = async (imei) => {
    if (!imei || imei.trim().length === 0) {
      setImeiValidation({ status: '', message: '', loading: false });
      return;
    }

    if (!/^\d{15}$/.test(imei.trim())) {
      setImeiValidation({ 
        status: 'error', 
        message: 'IMEI 格式錯誤，應為15位數字', 
        loading: false 
      });
      return;
    }

    setImeiValidation({ status: '', message: '', loading: true });

    try {
      const result = await Gas.validateIMEI(imei.trim(), machineID);

      if (result.valid) {
        setImeiValidation({ 
          status: 'success', 
          message: 'IMEI 驗證通過', 
          loading: false 
        });
      } else {
        setImeiValidation({ 
          status: 'error', 
          message: result.message || 'IMEI 驗證失敗', 
          loading: false 
        });
      }
    } catch (error) {
      console.error('IMEI 驗證失敗:', error);
      setImeiValidation({ 
        status: 'warning', 
        message: '無法驗證 IMEI，但仍可儲存', 
        loading: false 
      });
    }
  };

  const handleImeiChange = (e) => {
    const value = e.target.value;
    setDeviceImei(value);
    
    clearTimeout(window.imeiValidationTimer);
    window.imeiValidationTimer = setTimeout(() => {
      validateIMEI(value);
    }, 500);
  };

  const getActiveSensors = () => {
    const activeSensors = [];
    
    for (let i = 1; i <= 8; i++) {
      const sensorKey = `sensor${i}`;
      const configValue = sensorConfigs[sensorKey]?.name;
      const formValue = form.getFieldValue(`sensorName${i}`);
      const sensorName = configValue || formValue || '';
      
      if (sensorName && sensorName.trim()) {
        activeSensors.push({
          key: sensorKey,
          name: sensorName.trim(),
          value: sensorConfigs[sensorKey]?.value || "--",
          dbKey: sensorConfigs[sensorKey]?.dbKey,
          bounds: sensorConfigs[sensorKey]?.bounds,
          calibration: sensorConfigs[sensorKey]?.calibration,
          ...sensorConfigs[sensorKey]
        });
      }
    }

    if (showHeatRisk && sensorConfigs['sensor9']) {
      activeSensors.push({
        key: 'sensor9',
        name: '熱指數',
        value: sensorConfigs['sensor9']?.value || "--",
        dbKey: 'heat_index',
        bounds: sensorConfigs['sensor9']?.bounds,
        calibration: sensorConfigs['sensor9']?.calibration,
        ...sensorConfigs['sensor9']
      });
    }
    
    return activeSensors;
  };

  const refreshData = async () => {
    setRefreshing(true);
    try {
      const data = await Gas.fetchGasData(machineID);
      setState(data.info);
      
      setSensorConfigs(prev => {
        const updated = { ...prev };
        data.info.sensors.forEach((sensor, index) => {
          const sensorKey = `sensor${index + 1}`;
          if (updated[sensorKey]) {
            updated[sensorKey] = {
              ...updated[sensorKey],
              value: sensor.value ?? "--",
              rawValue: sensor.value ?? "--"
            };
          }
        });

        if (data.info.show_heat_risk === 1 && data.info.sensors[8] && updated['sensor9']) {
          updated['sensor9'] = {
            ...updated['sensor9'],
            value: data.info.sensors[8].value ?? "--",
            rawValue: data.info.sensors[8].value ?? "--"
          };
        }

        return updated;
      });
      
      message.success("資料已成功刷新");
    } catch (error) {
      console.error("刷新資料失敗:", error);
      message.error("刷新資料失敗，請稍後再試");
    } finally {
      setRefreshing(false);
    }
  };

  const openRefreshModal = () => {
    Modal.confirm({
      title: "確認刷新",
      content: `確認刷新 ${machineInfo.name} 感測器資料？`,
      onOk: refreshData,
      onCancel: () => message.info("刷新操作已取消")
    });
  };

  // 本元件是表格的一個 <td>，一頁會有多個實例，
  // 因此警報設定改為「開啟 Modal 時才載入」，避免列表渲染時打出 N 個請求
  const openAlertModal = async () => {
    setOnAlertModal(true);
    setAlertLoading(true);
    try {
      const cfg = await Gas.getAlertConfig(machineID);
      setForceBufferActive(!!cfg.force_buffer_active);
      alertForm.setFieldsValue({
        alertEnabled: cfg.alert_enabled,
        cooldownMinutes: cfg.alert_cooldown_min,
        weekdays: bitmaskToWeekdays(cfg.alert_weekdays),
        timeRange: [
          dayjs(cfg.alert_time_start, "HH:mm"),
          dayjs(cfg.alert_time_end, "HH:mm"),
        ],
        heatRiskLevel: cfg.alert_heat_risk_level,
        sendToBuffer: cfg.send_to_buffer,
      });
    } catch (error) {
      console.error("載入警報設定失敗:", error);
      message.error("載入警報設定失敗");
      // 給一組安全預設，讓使用者仍可設定後儲存
      alertForm.setFieldsValue({
        alertEnabled: false,
        cooldownMinutes: 20,
        weekdays: [0, 1, 2, 3, 4, 5, 6],
        timeRange: [dayjs("00:00", "HH:mm"), dayjs("23:59", "HH:mm")],
        heatRiskLevel: 3,
        sendToBuffer: false,
      });
    } finally {
      setAlertLoading(false);
    }
  };

  const applyAlertPreset = (presetKey) => {
    const preset = ALERT_PRESETS[presetKey];
    if (!preset) return;

    const next = {};
    if (preset.weekdays) next.weekdays = preset.weekdays;
    if (preset.start && preset.end) {
      next.timeRange = [dayjs(preset.start, "HH:mm"), dayjs(preset.end, "HH:mm")];
    }
    alertForm.setFieldsValue(next);
    message.info(`已套用：${preset.label}（記得按儲存）`);
  };

  const onSubmitAlertConfig = async (values) => {
    const weekdays = values.weekdays || [];

    if (values.alertEnabled && weekdays.length === 0) {
      message.error("已啟用通知時，至少需勾選一個發報星期");
      return;
    }
    if (!values.timeRange || !values.timeRange[0] || !values.timeRange[1]) {
      message.error("請設定發報時間");
      return;
    }

    setAlertSaving(true);
    try {
      await Gas.updateAlertConfig(machineID, {
        alert_enabled: !!values.alertEnabled,
        alert_cooldown_min: values.cooldownMinutes,
        alert_weekdays: weekdaysToBitmask(weekdays),
        alert_time_start: values.timeRange[0].format("HH:mm"),
        alert_time_end: values.timeRange[1].format("HH:mm"),
        alert_heat_risk_level: values.heatRiskLevel ?? 0,
        send_to_buffer: !!values.sendToBuffer,
      });
      message.success("警報設定已更新");
      setOnAlertModal(false);
    } catch (error) {
      message.error(error.message || "更新警報設定失敗");
    } finally {
      setAlertSaving(false);
    }
  };

  const handleRainToggle = (sensorIndex, checked) => {
    setRainFlags(prev => ({ ...prev, [sensorIndex]: checked }));
    if (checked) {
      // 雨量計固定不校正：強制 A=1 / B=0（後端儲存時也會再次強制）
      form.setFieldsValue({
        [`sensor${sensorIndex}A`]: 1,
        [`sensor${sensorIndex}B`]: 0
      });
    }
  };

  const handleBoundToggle = (sensorIndex, side, checked) => {
    // side: 'ub' 上限 / 'lb' 下限。停用時只 disable 欄位、保留數值，重新勾選即恢復原設定
    setBoundFlags(prev => ({
      ...prev,
      [sensorIndex]: { ub: true, lb: true, ...prev[sensorIndex], [side]: checked }
    }));
  };

  const handleSensorNameChange = (sensorIndex, value) => {
    const sensorKey = `sensor${sensorIndex}`;
    
    setSensorConfigs(prev => ({
      ...prev,
      [sensorKey]: {
        ...prev[sensorKey],
        name: value || '',
      }
    }));
    form.setFieldValue(`sensorName${sensorIndex}`, value || '');
  };

  const onSubmitSettings = async (values) => {
    try {
      setLoading(true);

      // 驗證感測器名稱格式
      for (let i = 1; i <= 8; i++) {
        const sensorName = values[`sensorName${i}`];
        const mqttField = values[`sensorMqtt${i}`];

        if (sensorName && sensorName.trim() && !validateFieldName(sensorName)) {
          message.error(`感測器${i}顯示名稱包含不允許的字符(不可使用引號)`);
          setLoading(false);
          return;
        }

        if (mqttField && mqttField.trim() && !validateFieldName(mqttField)) {
          message.error(`感測器${i} MQTT字串包含不允許的字符(不可使用引號)`);
          setLoading(false);
          return;
        }
      }

      const sensorsConfig = {};

      for (let i = 1; i <= 8; i++) {
        const sensorKey = `sensor${i}`;
        const sensorLetter = SENSOR_MAPPING[sensorKey].dbKey;
        const sensorName = values[`sensorName${i}`];
        const mqttField = values[`sensorMqtt${i}`];
        
        const isRain = !!values[`sensor${i}IsRain`];

        sensorsConfig[sensorLetter] = {
          name: sensorName === undefined ? null : (sensorName === "" ? "" : String(sensorName).trim()),
          mqtt_field: mqttField === undefined ? null : (mqttField === "" ? "" : String(mqttField).trim()),
          is_rain: isRain,
          // 雨量計固定 A=1/B=0（後端亦會強制，此處為一致性）
          calibration: isRain ? { A: 1, B: 0 } : {
            A: values[`${sensorKey}A`] || 1,
            B: values[`${sensorKey}B`] || 0
          },
          bounds: {
            upperBound: values[`${sensorKey}UpperBound`] ?? 100,
            lowerBound: values[`${sensorKey}LowerBound`] ?? 0,
            tolerance: values[`${sensorKey}Tolerance`] ?? 1,
            upperEnabled: values[`${sensorKey}UbOn`] !== false,
            lowerEnabled: values[`${sensorKey}LbOn`] !== false
          }
        };
      }

      const requestData = {
        sensors: sensorsConfig
      };

      if (values.mqttTopic) {
        requestData.mqtt_topic = values.mqttTopic;
      }
      if (values.deviceImei && values.deviceImei.trim()) {
        requestData.device_imei = values.deviceImei.trim();
      }

      requestData.show_heat_risk = values.showHeatRisk !== false;
      requestData.show_heat_lines = values.showHeatLines !== false;

      const result = await Gas.updateDeviceConfig(machineID, requestData);

      // 更新本地狀態
      const updatedConfigs = { ...sensorConfigs };
      
      for (let i = 1; i <= 8; i++) {
        const sensorKey = `sensor${i}`;
        const sensorName = values[`sensorName${i}`] || "";
        const mqttField = values[`sensorMqtt${i}`] || "";
        const isRain = !!values[`sensor${i}IsRain`];
        const newCalibrationA = isRain ? 1 : (values[`${sensorKey}A`] || 1);
        const newCalibrationB = isRain ? 0 : (values[`${sensorKey}B`] || 0);
        
        // DB 存的值已是後端套用校正後的結果，前端直接使用，不重複套用
        const rawValue = updatedConfigs[sensorKey]?.rawValue || updatedConfigs[sensorKey]?.value;
        
        updatedConfigs[sensorKey] = {
          ...updatedConfigs[sensorKey],
          name: sensorName.trim() || "",
          mqttField: mqttField.trim() || "",
          value: rawValue,
          rawValue: rawValue,
          isRain: isRain,
          calibration: {
            A: newCalibrationA,
            B: newCalibrationB
          },
          bounds: {
            upperBound: values[`${sensorKey}UpperBound`] ?? 100,
            lowerBound: values[`${sensorKey}LowerBound`] ?? 0,
            tolerance: values[`${sensorKey}Tolerance`] ?? 1,
            upperEnabled: values[`${sensorKey}UbOn`] !== false,
            lowerEnabled: values[`${sensorKey}LbOn`] !== false
          }
        };
      }

      if (updatedConfigs['sensor9']) {
        updatedConfigs['sensor9'] = {
          ...updatedConfigs['sensor9'],
          levelLines: values.showHeatLines !== false
        };
      }

      setSensorConfigs(updatedConfigs);

      if (requestData.mqtt_topic) {
        setMqttTopic(requestData.mqtt_topic);
      }
      if (requestData.device_imei) {
        setDeviceImei(requestData.device_imei);
      }

      setShowHeatRisk(values.showHeatRisk !== false);

      // 更新熱危害發佈主題
      const newHeatRiskTopic = values.heatRiskMqttTopic || '';
      if (newHeatRiskTopic !== heatRiskMqttTopic) {
        try {
          const topicResult = await GasMonitor.UpdateHeatRiskTopic(machineID, newHeatRiskTopic);
          if (topicResult.code === 200) {
            setHeatRiskMqttTopic(newHeatRiskTopic);
          } else {
            message.warning('熱危害發佈主題更新失敗，但其他設定已保存');
          }
        } catch (error) {
          console.error('更新熱危害發佈主題失敗:', error);
          message.warning('熱危害發佈主題更新失敗，但其他設定已保存');
        }
      }

      message.success("設定已更新");
      setOnSettingModal(false);

      // 重新獲取最新數據並應用校正
      try {
        const data = await Gas.fetchGasData(machineID);
        setState(data.info);

        setSensorConfigs(prev => {
          const updated = { ...prev };
          data.info.sensors.forEach((sensor, index) => {
            const sensorKey = `sensor${index + 1}`;
            if (updated[sensorKey]) {
              const rawValue = sensor.value ?? "--";
              // DB 存的值已是後端套用校正後的結果，前端直接使用，不重複套用
              
              updated[sensorKey] = {
                ...updated[sensorKey],
                value: rawValue,
                rawValue: rawValue
              };
            }
          });

          if (values.showHeatRisk && data.info.sensors[8]) {
            updated['sensor9'] = {
              name: '熱指數',
              mqttField: 'heat_index',
              value: data.info.sensors[8].value ?? "--",
              rawValue: data.info.sensors[8].value ?? "--",
              levelLines: values.showHeatLines !== false,
              calibration: { A: 1, B: 0 },
              dbKey: 'heat_index'
            };
          } else if (!values.showHeatRisk && updated['sensor9']) {
            // 如果關閉熱指數,移除 sensor9
            delete updated['sensor9'];
          }
          
          return updated;
        });
      } catch (refreshError) {
        console.warn('刷新感測器數值失敗，但設定已保存:', refreshError);
      }
      
    } catch (error) {
      console.error("更新設定失敗:", error);
      message.error(`更新設定失敗: ${error.message}`);

      // 如果更新失敗，嘗試重新載入初始數據
      try {
        await loadInitialData();
      } catch (reloadError) {
        console.error('重新載入數據也失敗:', reloadError);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading && !state) {
    return (
      <td>
        <div className="d-flex justify-content-center py-2">
          <Spin size="large" />
        </div>
      </td>
    );
  }

  if (!state) {
    return (
      <td>
        <div className="d-flex justify-content-center py-2">
          <div>載入失敗</div>
        </div>
      </td>
    );
  }

  const activeSensors = getActiveSensors();

  return (
    <React.Fragment>
      <td>
        <div className="d-flex justify-content-center py-2">
          <div style={{ width: "80%" }}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: '8px',
              marginBottom: '8px',
              fontSize: '14px'
            }}>
              <div style={{ textAlign: 'center' }}>
                <span className="font-weight-bold">編號：</span>
                <span>{machineID}</span>
              </div>
              {showHeatRisk && (
                <div style={{ textAlign: 'center' }}>
                  <span className="font-weight-bold">熱危害等級：</span>
                  {getHeatRiskText(state.level)}
                </div>
              )}
            </div>

            {/* 動態顯示有名稱的感測器 */}
            {activeSensors.length > 0 ? (
              <div>
                {activeSensors
                  .reduce((acc, sensor, index) => {
                    if (index % 2 === 0) acc.push(activeSensors.slice(index, index + 2));
                    return acc;
                  }, [])
                  .map((group, groupIndex) => (
                    <div key={groupIndex} style={{ 
                      display: 'grid', 
                      gridTemplateColumns: group.length === 2 ? '1fr 1fr' : '1fr', 
                      gap: '8px',
                      marginBottom: '8px',
                      fontSize: '14px'
                    }}>
                      {group.map((sensor) => (
                        <div key={sensor.key} style={{ 
                          textAlign: 'center',
                          padding: '4px'
                        }}>
                          <span className="font-weight-bold">
                            {sensor.name}
                            {sensor.isRain && <Tag color="blue" style={{ marginLeft: 4, fontSize: '11px', lineHeight: '16px', padding: '0 4px' }}>近1hr</Tag>}
                            ：
                          </span>
                          <span>
                            {sensor.value === null || sensor.value === undefined ? "--" : 
                            (typeof sensor.value === 'number' ? sensor.value.toFixed(2) : sensor.value)}
                          </span>
                        </div>
                      ))}
                      {/* 如果是奇數個感測器，添加空白佔位 */}
                      {group.length === 1 && <div className="flex-grow-1" style={{ minWidth: '45%' }}></div>}
                    </div>
                  ))}
              </div>
            ) : (
              <div className="text-center mb-2">
                <span className="text-muted">未配置感測器名稱</span>
              </div>
            )}

            <div className="d-flex justify-content-center mb-2">
              {user.class >= process.env.REACT_APP_EDITOR_CALSS_LV && (
                <button className="control_setting" onClick={() => setOnSettingModal(true)}>
                  <SettingIcon className="button_svg control_svg" />
                </button>
              )}
              <button 
                className="control_setting" 
                onClick={openRefreshModal}
                disabled={refreshing}
              >
                <ReTryIcon className="button_svg control_svg" />
              </button>
              {activeSensors.length > 0 && (
                <button className="control_setting" onClick={() => setOnChartModal(true)}>
                  <ChartIcon className="button_svg control_svg" />
                </button>
              )}
              {/* 警報發報設定：權限與設定按鈕一致，因為這會影響整個場域收不收得到警報 */}
              {user.class >= process.env.REACT_APP_EDITOR_CALSS_LV && (
                <Tooltip title="警報發報設定">
                  <button className="control_setting" onClick={openAlertModal}>
                    <ClockIcon className="button_svg control_svg" />
                  </button>
                </Tooltip>
              )}
            </div>
          </div>
        </div>

        {/* 設定 Modal */}
        <Modal 
          title="感測器設定" 
          open={onSettingModal} 
          onCancel={() => setOnSettingModal(false)}
          width="95%"
          style={{ top: 20 }}
          bodyStyle={{ maxHeight: window.innerHeight - 200, overflowX: 'auto' }}
          footer={[
            <Button key="cancel" onClick={() => setOnSettingModal(false)}>
              取消
            </Button>,
            <Button 
              key="submit" 
              type="default" style={{ background: "var(--content-background)", color: "var(--primary-text)", border: "2px solid var(--primary-border)" }} 
              loading={loading}
              onClick={() => form.submit()}
            >
              儲存設定
            </Button>
          ]}
          destroyOnClose
        >
          <Form 
            form={form} 
            onFinish={onSubmitSettings} 
            layout="vertical"
          >
            {/* MQTT 設定卡片 */}
            <Card title="MQTT 通訊設定" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item 
                    name="mqttTopic" 
                    label="MQTT訂閱主題"
                    rules={[{ required: true, message: "請輸入MQTT主題" }]}
                  >
                    <Input 
                      placeholder="請輸入MQTT主題" 
                      addonBefore="主題:"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item 
                    name="deviceImei" 
                    label={
                      <span>
                        設備 IMEI
                        <Tooltip title="15位數字的設備唯一識別碼，用於 MQTT 訊息識別">
                          <span style={{ marginLeft: 4, color: '#1890ff', cursor: 'help' }}>(?)</span>
                        </Tooltip>
                      </span>
                    }
                    validateStatus={imeiValidation.status}
                    help={imeiValidation.message}
                  >
                    <Input 
                      placeholder="請輸入15位數字IMEI" 
                      maxLength={15}
                      onChange={handleImeiChange}
                      suffix={imeiValidation.loading ? <Spin size="small" /> : null}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item 
                    name="showHeatRisk" 
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Checkbox>顯示熱危害等級</Checkbox>
                  </Form.Item>
                </Col>
                {form.getFieldValue('showHeatRisk') && (
                  <Col span={24}>
                    <Form.Item 
                      name="showHeatLines" 
                      valuePropName="checked"
                      style={{ marginBottom: 8 }}
                      tooltip="熱指數圖表顯示第二級 32.2、第三級 40.6、第四級 54.4 的分界虛線（僅影響圖表，熱危害發報請至警報設定調整）"
                    >
                      <Checkbox>圖表顯示熱危害分級虛線</Checkbox>
                    </Form.Item>
                  </Col>
                )}
                {form.getFieldValue('showHeatRisk') && (
                  <Col span={24}>
                    <Form.Item 
                      name="heatRiskMqttTopic"
                      label="熱危害發佈主題"
                      tooltip="發佈熱危害等級 0-5 的 MQTT 主題"
                    >
                      <Input placeholder="例如：heat_risk/device_123" />
                    </Form.Item>
                  </Col>
                )}
              </Row>
            </Card>

            {/* 感測器設定卡片 */}
            <Card title="感測器配置" style={{ marginBottom: 16 }}>
              {Array.from({ length: 8 }, (_, i) => {
                const sensorIndex = i + 1;
                const sensorKey = `sensor${sensorIndex}`;
                const sensorName = form.getFieldValue(`sensorName${sensorIndex}`) || "";
                const isActive = sensorName.trim() !== "";
                const isCollapsed = collapsedSensors[sensorKey];
                  
                return (
                  <Card 
                    key={sensorIndex}
                    size="small"
                    title={
                      <div 
                        style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          cursor: 'pointer'
                        }}
                        onClick={() => setCollapsedSensors(prev => ({
                          ...prev,
                          [sensorKey]: !prev[sensorKey]
                        }))}
                      >
                        <span style={{ color: isActive ? 'var(--ant-primary-color)' : 'var(--secondary-text)', fontWeight: isActive ? 600 : 400 }}>
                          感測器 {sensorIndex}
                          {isActive && ` (${sensorName})`}
                        </span>
                        <span style={{ 
                          fontSize: '16px',
                          transform: isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)',
                          transition: 'transform 0.2s'
                        }}>
                          ▶
                        </span>
                      </div>
                    }
                    style={{ 
                      marginBottom: 12,
                      // 顏色跟主題走：原本寫死淺綠／淺灰，深色主題下白字貼淺底會看不見
                      border: isActive ? '2px solid var(--ant-primary-color)' : '1px solid var(--border-color)',
                      backgroundColor: isActive ? 'var(--table-cell-highlight)' : 'transparent'
                    }}
                  >
                    {!isCollapsed && (
                      <>
                        {/* 感測器名稱 */}
                        <Row gutter={[8, 8]}>
                          <Col xs={24} sm={6}>
                            <Form.Item 
                              name={`sensorName${sensorIndex}`} 
                              label="感測器名稱"
                              style={{ marginBottom: 8 }}
                              tooltip="網頁顯示的名稱，例如：溫度、Temperature"
                            >
                              <Input 
                                placeholder="例如：溫度"
                                onChange={(e) => handleSensorNameChange(sensorIndex, e.target.value)}
                                size="small"
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={6}>
                            <Form.Item 
                              name={`sensorMqtt${sensorIndex}`} 
                              label="MQTT 字串"
                              style={{ marginBottom: 8 }}
                              tooltip="MQTT 訊息中的欄位名稱，例如：temp、TMP"
                            >
                              <Input 
                                placeholder={SENSOR_NAME_SUGGESTIONS[i] || ""}
                                size="small"
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={8}>
                            <Form.Item
                              name={`sensor${sensorIndex}IsRain`}
                              label="雨量計"
                              valuePropName="checked"
                              style={{ marginBottom: 8 }}
                              tooltip="雨量計以累積值(mm)回傳，系統會自動差分：圖表按整點顯示時雨量、狀態列與警報取近60分鐘雨量。勾選後固定不套用校正(A=1/B=0)。"
                            >
                              <Checkbox onChange={(e) => handleRainToggle(sensorIndex, e.target.checked)}>
                                累積值回傳（雨量計）
                              </Checkbox>
                            </Form.Item>
                          </Col>
                        </Row>

                        {rainFlags[sensorIndex] && (
                          <Alert
                            message="雨量計模式"
                            description="DB 儲存原始累積值，顯示與警報由系統自動差分計算。校正係數固定 A=1/B=0；「上限」代表近60分鐘雨量門檻(mm)，下限與下限放寬不適用。"
                            type="info"
                            showIcon
                            style={{ marginBottom: 8 }}
                          />
                        )}

                        {/* 邊界值設定：上限、下限各自獨立啟用 */}
                        <Row gutter={[8, 0]}>
                          <Col xs={12} sm={6}>
                            <Form.Item
                              name={`sensor${sensorIndex}UbOn`}
                              valuePropName="checked"
                              initialValue={true}
                              style={{ marginBottom: 4 }}
                              tooltip="取消勾選後：圖表不顯示上限虛線，且不再發送超出上限警報。數值會保留。"
                            >
                              <Checkbox onChange={(e) => handleBoundToggle(sensorIndex, 'ub', e.target.checked)}>
                                啟用上限
                              </Checkbox>
                            </Form.Item>
                          </Col>
                          <Col xs={12} sm={6}>
                            <Form.Item
                              name={`sensor${sensorIndex}LbOn`}
                              valuePropName="checked"
                              initialValue={true}
                              style={{ marginBottom: 4 }}
                              tooltip="取消勾選後：圖表不顯示下限與下限放寬虛線，且不再發送低於下限警報。數值會保留。"
                            >
                              <Checkbox
                                disabled={rainFlags[sensorIndex]}
                                onChange={(e) => handleBoundToggle(sensorIndex, 'lb', e.target.checked)}
                              >
                                啟用下限（含放寬）
                              </Checkbox>
                            </Form.Item>
                          </Col>
                        </Row>
                        <Row gutter={[8, 8]}>
                          <Col xs={12} sm={6}>
                            <Form.Item 
                              name={`sensor${sensorIndex}UpperBound`} 
                              label="上限"
                              initialValue={100}
                              style={{ marginBottom: 8 }}
                            >
                              <InputNumber 
                                style={{ width: "100%" }} 
                                placeholder="上限值"
                                step={0.1}
                                size="small"
                                disabled={boundFlags[sensorIndex]?.ub === false}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={12} sm={6}>
                            <Form.Item 
                              name={`sensor${sensorIndex}LowerBound`} 
                              label={rainFlags[sensorIndex] ? "下限（雨量不適用）" : "下限"}
                              initialValue={0}
                              style={{ marginBottom: 8 }}
                            >
                              <InputNumber 
                                style={{ width: "100%" }} 
                                placeholder="下限值"
                                step={0.1}
                                size="small"
                                disabled={rainFlags[sensorIndex] || boundFlags[sensorIndex]?.lb === false}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={12} sm={6}>
                            <Form.Item 
                              name={`sensor${sensorIndex}Tolerance`} 
                              label={rainFlags[sensorIndex] ? "下限放寬（雨量不適用）" : "下限放寬"}
                              initialValue={1}
                              style={{ marginBottom: 8 }}
                            >
                              <InputNumber 
                                style={{ width: "100%" }} 
                                placeholder="放寬值"
                                step={0.1}
                                size="small"
                                disabled={rainFlags[sensorIndex] || boundFlags[sensorIndex]?.lb === false}
                              />
                            </Form.Item>
                          </Col>
                        </Row>

                        {/* 校正參數 - 響應式布局（雨量計固定 A=1/B=0，隱藏輸入框） */}
                        {!rainFlags[sensorIndex] && (
                          <Row gutter={[8, 8]}>
                            <Col xs={12} sm={6}>
                              <Form.Item 
                                name={`sensor${sensorIndex}A`} 
                                label="校正係數 A"
                                initialValue={1}
                                style={{ marginBottom: 8 }}
                              >
                                <InputNumber 
                                  style={{ width: "100%" }} 
                                  placeholder="係數A"
                                  step={0.1}
                                  size="small"
                                  disabled={!isActive}
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={12} sm={6}>
                              <Form.Item 
                                name={`sensor${sensorIndex}B`} 
                                label="偏移量 B"
                                initialValue={0}
                                style={{ marginBottom: 8 }}
                              >
                                <InputNumber 
                                  style={{ width: "100%" }} 
                                  placeholder="偏移B"
                                  step={0.1}
                                  size="small"
                                  disabled={!isActive}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        )}
                      </>
                    )}
                  </Card>
                );
              })}
              
              {/* 說明信息 */}
              <Alert
                message="感測器配置說明"
                description="每個感測器都有獨立的上下限和下限放寬值。只有設定名稱的感測器會在界面中顯示資料和圖表。校正公式：輸出值 = 原始值 × 係數A + 偏移B"
                type="info"
                style={{ marginTop: 16 }}
              />

              <Alert
                message="IMEI 設定說明"
                description="IMEI 是設備的唯一識別碼，當 MQTT 訊息中包含 IMEI 時，系統會優先使用 IMEI 來識別設備。如果未設定或 MQTT 訊息中沒有 IMEI，系統會使用主題名稱來識別設備。"
                type="info"
                style={{ marginTop: 16 }}
              />
            </Card>
          </Form>
        </Modal>

        {/* 圖表 Modal */}
        {activeSensors.length > 0 && (
          <GasChart
            id={machineID}
            visible={onChartModal}
            setVisible={setOnChartModal}
            sensorConfigs={sensorConfigs}
            onConfigChange={setSensorConfigs}
          />
        )}

        {/* 警報發報設定 Modal */}
        <Modal
          title="警報發報設定"
          open={onAlertModal}
          onCancel={() => setOnAlertModal(false)}
          width={560}
          style={{ top: 20 }}
          footer={[
            <Button key="cancel" onClick={() => setOnAlertModal(false)}>
              取消
            </Button>,
            <Button
              key="submit"
              type="default"
              style={{
                background: "var(--content-background)",
                color: "var(--primary-text)",
                border: "2px solid var(--primary-border)",
              }}
              loading={alertSaving}
              onClick={() => alertForm.submit()}
            >
              儲存設定
            </Button>,
          ]}
          destroyOnClose
        >
          <Spin spinning={alertLoading}>
            <Form form={alertForm} layout="vertical" onFinish={onSubmitAlertConfig}>
              {forceBufferActive && (
                <Alert
                  type="error"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message="全域強制導向 Buffer 群"
                  description="伺服器目前設定為將所有警報導向 Buffer 群，以下的個別設定會被覆蓋。請聯絡系統管理員。"
                />
              )}

              <Form.Item
                name="alertEnabled"
                label="啟用異常通知"
                valuePropName="checked"
                tooltip="關閉後，此設備偵測到異常也不會發送 LINE / Telegram 通知"
              >
                <Switch checkedChildren="開" unCheckedChildren="關" />
              </Form.Item>

              <Form.Item
                name="cooldownMinutes"
                label="警報間隔（CD 時間）"
                tooltip="同一設備的同一項目異常時，間隔內只會發送一則通知。冷卻期間的異常會改送 Buffer 群，不會遺失。"
                rules={[
                  { required: true, message: "請輸入警報間隔" },
                  { type: "number", min: 1, max: 1440, message: "需介於 1~1440 分鐘" },
                ]}
              >
                <InputNumber min={1} max={1440} addonAfter="分鐘" style={{ width: 220 }} />
              </Form.Item>

              <Form.Item
                name="heatRiskLevel"
                label="熱危害等級警報"
                tooltip="達到所選等級以上時發報。需在感測器設定中啟用「顯示熱危害等級」才會生效。"
              >
                <Select options={HEAT_RISK_OPTIONS} style={{ width: 260 }} />
              </Form.Item>

              <Divider style={{ margin: "12px 0" }}>發報時間規則</Divider>

              <div style={{ marginBottom: 12 }}>
                <span style={{ marginRight: 8, color: "#666" }}>快速設定：</span>
                <Space wrap size={[8, 8]}>
                  <Button size="small" onClick={() => applyAlertPreset("allDay")}>
                    全天候
                  </Button>
                  <Button size="small" onClick={() => applyAlertPreset("weekday")}>
                    週末不發
                  </Button>
                  <Button size="small" onClick={() => applyAlertPreset("workHour")}>
                    下班時間不發
                  </Button>
                </Space>
              </div>

              <Form.Item
                name="weekdays"
                label="發報星期"
                tooltip="只有勾選的星期會發送通知"
              >
                <Checkbox.Group options={WEEKDAY_OPTIONS} />
              </Form.Item>

              <Form.Item
                name="timeRange"
                label="發報時間"
                tooltip="設定「允許發報」的時段。若起始時間晚於結束時間（例如 20:00~06:00），系統會視為跨午夜的夜班時段。"
                rules={[{ required: true, message: "請設定發報時間" }]}
              >
                {/* order={false} 不可省略：antd 預設會自動排序，
                    會讓 20:00~06:00 被改成 06:00~20:00，跨午夜時段就設不出來 */}
                <TimePicker.RangePicker
                  format="HH:mm"
                  minuteStep={5}
                  order={false}
                  allowClear={false}
                  style={{ width: "100%" }}
                  placeholder={["開始時間", "結束時間"]}
                />
              </Form.Item>

              <Divider style={{ margin: "12px 0" }} />

              <Form.Item
                name="sendToBuffer"
                valuePropName="checked"
                tooltip="勾選後，此設備的所有警報改送 Telegram Buffer 群，現場群組不會收到。適合新設備上線前的驗證。"
              >
                <Checkbox>發到 Buffer 群（現場群組不發送）</Checkbox>
              </Form.Item>

              <AlertSchedulePreview form={alertForm} />
            </Form>
          </Spin>
        </Modal>
      </td>
    </React.Fragment>
  );
}